import { Box, Text, TextButton } from '@wix/design-system';
import { Trans, useTranslation } from '@wix/fe-essentials/react';
import { CollapseList, OverviewSection, OverviewSectionSkins, OverviewSectionWeights } from '@wix/rules-panel-ui';
import { Confirm } from '@wix/wix-ui-icons-common';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo } from 'react';
import { activeTabIdAtom, entryPointInfoAtom, isScanningAtom, openCardGroupsAtom, openPagesAtom, scrollerNodeRefAtom, totalIssuesCountAtom } from '../atoms/session-atoms';
import { BiCtaNames } from '../bi/types';
import { A11yCardWrapper } from '../components/wizard-content/content-results/card-groups/a11y-card-wrapper/a11y-card-wrapper';
import { CollapseActionsProvider, ItemScopeProvider } from '../components/wizard-content/content-results/card-groups/lists/context/collapse-actions-context';
import { useHandleUnsavedChangesOnToggle } from '../components/wizard-content/content-results/card-groups/lists/utils/use-handle-unsaved-changes-on-toggle';
import { ManualTasksCard } from '../components/wizard-content/content-results/card-groups/manual-tasks-card';
import { ACCESSIBILITY_WIZARD_TAB_IDS } from '../consts';
import { usePanelStateAtom } from '../hooks/rules-state';
import { useBiLogger } from '../hooks/use-bi-logger';
import { dataHooks } from '../utils/datahooks';
export const CardsIssueList = _ref => {
  let {
    unsavedChangesHandlers
  } = _ref;
  const {
    getPagesData
  } = usePanelStateAtom();
  const parentRef = useAtomValue(scrollerNodeRefAtom);
  const entryPointInfo = useAtomValue(entryPointInfoAtom);
  const pagesData = getPagesData();
  const [openItems, setOpenItems] = useAtom(openCardGroupsAtom);
  const setOpenPages = useSetAtom(openPagesAtom);
  const wrappedSetOpenItems = useCallback(newOpenItems => {
    setOpenItems(newOpenItems);
    setOpenPages(new Set([]));
  }, [setOpenItems, setOpenPages]);
  const handleSetOpenItems = useHandleUnsavedChangesOnToggle(openItems, wrappedSetOpenItems, undefined, unsavedChangesHandlers);
  const sectionMap = useMemo(() => {
    const map = new Map();
    pagesData.forEach((_ref2, pageId) => {
      var _map$get;
      let {
        dividerTitle
      } = _ref2;
      if (!map.has(dividerTitle)) {
        map.set(dividerTitle, []);
      }
      (_map$get = map.get(dividerTitle)) == null || _map$get.push(pageId);
    });
    return map;
  }, [pagesData]);
  useEffect(() => {
    if (entryPointInfo && pagesData.has(entryPointInfo.pageId)) {
      const pageData = pagesData.get(entryPointInfo.pageId);
      if (pageData != null && pageData.dividerTitle) {
        setOpenItems(new Set([pageData.dividerTitle]));
      }
    }
  }, [entryPointInfo, pagesData, setOpenItems]);
  const isScanning = useAtomValue(isScanningAtom);
  const issuesNumber = useAtomValue(totalIssuesCountAtom);
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const [, setCurrentTabId] = useAtom(activeTabIdAtom);
  const goToManualTasks = () => {
    setCurrentTabId(ACCESSIBILITY_WIZARD_TAB_IDS.MANUAL_TASKS);
    biLogger.ctaClick({
      ctaName: BiCtaNames.GoToManualTasks
    });
  };
  const cardGroups = useMemo(() => {
    return Array.from(sectionMap.entries()).map(_ref3 => {
      let [dividerTitle, pageIds] = _ref3;
      const isCardOpen = openItems.has(dividerTitle);
      return /*#__PURE__*/React.createElement(ItemScopeProvider, {
        id: dividerTitle,
        key: dividerTitle
      }, /*#__PURE__*/React.createElement(A11yCardWrapper, {
        cardTitle: dividerTitle,
        isCardOpen: isCardOpen,
        pageIds: pageIds
      }));
    });
  }, [openItems, sectionMap]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    padding: "0 SP4 SP4 SP4"
  }, /*#__PURE__*/React.createElement(OverviewSection, {
    dataHook: dataHooks.overviewSectionDetectedIssues,
    description: issuesNumber > 0 || isScanning ? t(isScanning && issuesNumber === 0 ? 'accessibility-wizard.detected-issues.overview-section.loading.description' : 'accessibility-wizard.detected-issues.overview-section.description') : /*#__PURE__*/React.createElement(Text, {
      size: "tiny",
      weight: "thin",
      secondary: true
    }, /*#__PURE__*/React.createElement(Trans, {
      components: {
        Link: /*#__PURE__*/React.createElement(TextButton, {
          size: "tiny",
          skin: "dark",
          underline: "always",
          weight: "thin",
          onClick: goToManualTasks
        })
      },
      i18nKey: "accessibility-wizard.detected-issues.overview-section.go-to-manual",
      t: t
    })),
    detectedIssues: isScanning ? issuesNumber === 0 ? '-' : issuesNumber : issuesNumber === 0 ? /*#__PURE__*/React.createElement(Box, {
      color: "G10"
    }, /*#__PURE__*/React.createElement(Confirm, null)) : issuesNumber,
    showShadow: false,
    skin: issuesNumber === 0 && !isScanning ? OverviewSectionSkins.success : OverviewSectionSkins.warning,
    title: t(isScanning && issuesNumber === 0 ? 'accessibility-wizard.detected-issues.overview-section.loading.title' : issuesNumber > 0 ? 'accessibility-wizard.detected-issues.overview-section.title' : 'accessibility-wizard.detected-issues.overview-section.title.no-issues', {
      count: issuesNumber
    }),
    weight: OverviewSectionWeights.light
  })), /*#__PURE__*/React.createElement(Box, {
    align: "center",
    direction: "vertical",
    paddingLeft: "16px",
    paddingRight: "16px"
  }, /*#__PURE__*/React.createElement(CollapseActionsProvider, {
    setOpenItems: handleSetOpenItems
  }, /*#__PURE__*/React.createElement(CollapseList, {
    autoScroll: true,
    openItems: openItems,
    scrollContainerRef: parentRef ?? undefined,
    setOpenItems: handleSetOpenItems,
    showDividers: false,
    unsavedChangesHandlers: unsavedChangesHandlers
  }, cardGroups, /*#__PURE__*/React.createElement(ManualTasksCard, null)))));
};
//# sourceMappingURL=cards-issue-list.js.map